import { ContextManagerMethod, addTelemetryUsage, deepClone, makePublicApi, monitor, clocksNow, callMonitored, createHandlingStack, sanitize, createIdentityEncoder, displayAlreadyInitializedError, createTrackingConsentState, timeStampToClocks, CustomerContextKey, defineContextMethod, startBufferingData, isExperimentalFeatureEnabled, ExperimentalFeature, mockable, generateUUID, timeStampNow, } from '@datadog/browser-core';
import { ActionType, VitalType } from '../rawRumEvent.types';
import { createCustomVitalsState } from '../domain/vital/vitalCollection';
import { callPluginsMethod } from '../domain/plugins';
import { createPreStartStrategy } from './preStartRum';
import { startRum } from './startRum';
export function makeRumPublicApi(recorderApi, profilerApi, options = {}) {
    const trackingConsentState = createTrackingConsentState();
    const customVitalsState = createCustomVitalsState();
    const bufferedDataObservable = startBufferingData().observable;
    let strategy = createPreStartStrategy(options, trackingConsentState, customVitalsState, (configuration, deflateWorker, initialViewOptions, telemetry, hooks) => {
        const createEncoder = deflateWorker && options.createDeflateEncoder
            ? (streamId) => options.createDeflateEncoder(configuration, deflateWorker, streamId)
            : createIdentityEncoder;
        const startRumResult = mockable(startRum)(configuration, recorderApi, profilerApi, initialViewOptions, createEncoder, trackingConsentState, customVitalsState, bufferedDataObservable, telemetry, hooks, options.sdkName);
        recorderApi.onRumStart(startRumResult.lifeCycle, configuration, startRumResult.session, startRumResult.viewHistory, deflateWorker, startRumResult.telemetry);
        profilerApi.onRumStart(startRumResult.lifeCycle, startRumResult.hooks, configuration, startRumResult.session, startRumResult.viewHistory, createEncoder);
        strategy = createPostStartStrategy(strategy, startRumResult);
        callPluginsMethod(configuration.plugins, 'onRumStart', {
            strategy, // TODO: remove this in the next major release
            addEvent: startRumResult.addEvent,
            addError: startRumResult.addError,
        });
        return startRumResult;
    });
    const getStrategy = () => strategy;
    const startView = (options) => {
        const handlingStack = createHandlingStack('view');
        callMonitored(() => {
            const sanitizedOptions = typeof options === 'object' ? options : { name: options };
            strategy.startView({ ...sanitizedOptions, handlingStack });
            addTelemetryUsage({ feature: 'start-view' });
        });
    };
    const rumPublicApi = makePublicApi({
        init: (initConfiguration) => {
            const errorStack = new Error().stack;
            callMonitored(() => strategy.init(initConfiguration, rumPublicApi, errorStack));
        },
        setTrackingConsent: monitor((trackingConsent) => {
            trackingConsentState.update(trackingConsent);
            addTelemetryUsage({ feature: 'set-tracking-consent', tracking_consent: trackingConsent });
        }),
        setViewName: monitor((name) => {
            strategy.setViewName(name);
            addTelemetryUsage({ feature: 'set-view-name' });
        }),
        setViewContext: monitor((context) => {
            strategy.setViewContext(context);
            addTelemetryUsage({ feature: 'set-view-context' });
        }),
        setViewContextProperty: monitor((key, value) => {
            strategy.setViewContextProperty(key, value);
            addTelemetryUsage({ feature: 'set-view-context-property' });
        }),
        getViewContext: monitor(() => {
            addTelemetryUsage({ feature: 'set-view-context-property' });
            return strategy.getViewContext();
        }),
        getInternalContext: monitor((startTime) => strategy.getInternalContext(startTime)),
        getInitConfiguration: monitor(() => deepClone(strategy.initConfiguration)),
        addAction: (name, context) => {
            const handlingStack = createHandlingStack('action');
            callMonitored(() => {
                strategy.addAction({
                    name: sanitize(name),
                    context: sanitize(context),
                    startClocks: clocksNow(),
                    type: ActionType.CUSTOM,
                    handlingStack,
                });
                addTelemetryUsage({ feature: 'add-action' });
            });
        },
        startAction: monitor((name, options) => {
            // Check feature flag only after init; pre-init calls should be buffered
            if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_ACTION)) {
                return;
            }
            // addTelemetryUsage({ feature: 'start-action' })
            strategy.startAction(sanitize(name), {
                type: sanitize(options && options.type),
                context: sanitize(options && options.context),
                actionKey: options && options.actionKey,
            });
        }),
        stopAction: monitor((name, options) => {
            if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_ACTION)) {
                return;
            }
            // addTelemetryUsage({ feature: 'stop-action' })
            strategy.stopAction(sanitize(name), {
                type: sanitize(options && options.type),
                context: sanitize(options && options.context),
                actionKey: options && options.actionKey,
            });
        }),
        startResource: monitor((url, options) => {
            // Check feature flag only after init; pre-init calls should be buffered
            if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_RESOURCE)) {
                return;
            }
            // addTelemetryUsage({ feature: 'start-resource' })
            strategy.startResource(sanitize(url), {
                type: sanitize(options && options.type),
                method: sanitize(options && options.method),
                context: sanitize(options && options.context),
                resourceKey: options && options.resourceKey,
            });
        }),
        stopResource: monitor((url, options) => {
            if (strategy.initConfiguration && !isExperimentalFeatureEnabled(ExperimentalFeature.START_STOP_RESOURCE)) {
                return;
            }
            // addTelemetryUsage({ feature: 'stop-resource' })
            strategy.stopResource(sanitize(url), {
                type: sanitize(options && options.type),
                statusCode: options && options.statusCode,
                size: options && options.size,
                context: sanitize(options && options.context),
                resourceKey: options && options.resourceKey,
            });
        }),
        addError: (error, context) => {
            const handlingStack = createHandlingStack('error');
            callMonitored(() => {
                strategy.addError({
                    error, // Do not sanitize error here, it is needed unserialized by computeRawError()
                    handlingStack,
                    context: sanitize(context),
                    startClocks: clocksNow(),
                });
                addTelemetryUsage({ feature: 'add-error' });
            });
        },
        addTiming: monitor((name, time) => {
            // TODO: next major decide to drop relative time support or update its behaviour
            strategy.addTiming(sanitize(name), time);
        }),
        setViewLoadingTime: monitor(() => {
            const callTimestamp = timeStampNow();
            strategy.setLoadingTime(callTimestamp);
            addTelemetryUsage({
                feature: 'addViewLoadingTime',
            });
        }),
        setGlobalContext: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.setContext, 'set-global-context'),
        getGlobalContext: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.getContext, 'get-global-context'),
        setGlobalContextProperty: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.setContextProperty, 'set-global-context-property'),
        removeGlobalContextProperty: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.removeContextProperty, 'remove-global-context-property'),
        clearGlobalContext: defineContextMethod(getStrategy, CustomerContextKey.globalContext, ContextManagerMethod.clearContext, 'clear-global-context'),
        setUser: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.setContext, 'set-user'),
        getUser: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.getContext, 'get-user'),
        setUserProperty: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.setContextProperty, 'set-user-property'),
        removeUserProperty: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.removeContextProperty, 'remove-user-property'),
        clearUser: defineContextMethod(getStrategy, CustomerContextKey.userContext, ContextManagerMethod.clearContext, 'clear-user'),
        setAccount: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.setContext, 'set-account'),
        getAccount: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.getContext, 'get-account'),
        setAccountProperty: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.setContextProperty, 'set-account-property'),
        removeAccountProperty: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.removeContextProperty, 'remove-account-property'),
        clearAccount: defineContextMethod(getStrategy, CustomerContextKey.accountContext, ContextManagerMethod.clearContext, 'clear-account'),
        startView,
        stopSession: monitor(() => {
            strategy.stopSession();
            addTelemetryUsage({ feature: 'stop-session' });
        }),
        addFeatureFlagEvaluation: monitor((key, value) => {
            strategy.addFeatureFlagEvaluation(sanitize(key), sanitize(value));
            addTelemetryUsage({ feature: 'add-feature-flag-evaluation' });
        }),
        getSessionReplayLink: monitor(() => recorderApi.getSessionReplayLink()),
        startSessionReplayRecording: monitor((options) => {
            recorderApi.start(options);
            addTelemetryUsage({ feature: 'start-session-replay-recording', force: options && options.force });
        }),
        stopSessionReplayRecording: monitor(() => recorderApi.stop()),
        addDurationVital: (name, options) => {
            const handlingStack = createHandlingStack('vital');
            callMonitored(() => {
                addTelemetryUsage({ feature: 'add-duration-vital' });
                strategy.addDurationVital({
                    id: generateUUID(),
                    name: sanitize(name),
                    type: VitalType.DURATION,
                    startClocks: timeStampToClocks(options.startTime),
                    duration: options.duration,
                    context: sanitize(options && options.context),
                    description: sanitize(options && options.description),
                    handlingStack,
                });
            });
        },
        startDurationVital: (name, options) => {
            const handlingStack = createHandlingStack('vital');
            return callMonitored(() => {
                addTelemetryUsage({ feature: 'start-duration-vital' });
                return strategy.startDurationVital(sanitize(name), {
                    context: sanitize(options && options.context),
                    description: sanitize(options && options.description),
                    handlingStack,
                });
            });
        },
        stopDurationVital: monitor((nameOrRef, options) => {
            addTelemetryUsage({ feature: 'stop-duration-vital' });
            strategy.stopDurationVital(typeof nameOrRef === 'string' ? sanitize(nameOrRef) : nameOrRef, {
                context: sanitize(options && options.context),
                description: sanitize(options && options.description),
            });
        }),
        startFeatureOperation: (name, options) => {
            const handlingStack = createHandlingStack('vital');
            callMonitored(() => {
                addTelemetryUsage({ feature: 'add-operation-step-vital', action_type: 'start' });
                strategy.addOperationStepVital(name, 'start', { ...options, handlingStack });
            });
        },
        succeedFeatureOperation: monitor((name, options) => {
            addTelemetryUsage({ feature: 'add-operation-step-vital', action_type: 'succeed' });
            strategy.addOperationStepVital(name, 'end', options);
        }),
        failFeatureOperation: monitor((name, failureReason, options) => {
            addTelemetryUsage({ feature: 'add-operation-step-vital', action_type: 'fail' });
            strategy.addOperationStepVital(name, 'end', options, failureReason);
        }),
    });
    return rumPublicApi;
}
function createPostStartStrategy(preStartStrategy, startRumResult) {
    return {
        init: (initConfiguration) => {
            displayAlreadyInitializedError('DD_RUM', initConfiguration);
        },
        initConfiguration: preStartStrategy.initConfiguration,
        ...startRumResult,
    };
}
//# sourceMappingURL=rumPublicApi.js.map